<script setup>
    import {useTokenTransactionDownloads} from "@/composables/useTokenTransactionDownloads.js";
    import {computed, getCurrentInstance, ref} from "vue";
    import PerPageSelect from "@/components/ui/PerPageSelect.vue";
    import UserCompanies from "@/components/users/UserCompanies.vue";

    const props = defineProps({
        loading: { type: Boolean, default: false },
        meta: { type: Object, default: () => ({}) },
        data: { type: Array, default: () => [] },
        selectedIds: { type: Array, default: () => [] },
        selectionMode: { type: Boolean, default: false },
        detailsView: { type: Boolean, default: false },
        storageKey: { type: String, default: 'perPage_tokenTransactions' },
        showPerPage: { type: Boolean, default: true },
        paginated: { type: Boolean, default: true },
    })

    const { proxy } = getCurrentInstance()
    const auth = proxy.$auth

    const { download } = useTokenTransactionDownloads();

    const emits = defineEmits(['onPageChange', 'onToggleRow', 'onPerPageChange'])

    const perPage = ref(parseInt(localStorage.getItem(props.storageKey) || 10))

    const onPerPageChange = () => {
        localStorage.setItem(props.storageKey, String(perPage.value))
        emits('onPerPageChange', perPage.value)
    }

    const toggleRow = (transaction) => {
        emits('onToggleRow', transaction)
    }

    const canDownloadConfirmation = computed(() => {
         if (auth.isEmployee() && import.meta.env.VITE_ENABLE_TRANSACTION_CONFIRMATION_CLIENT !== 'true') {
             return false;
         }
         return true;
    })
</script>

<template>
    <b-table
        :data="loading ? [] : data"
        :loading="loading"

        :detailed="detailsView"
        :show-detail-icon="detailsView"
        detail-icon="chevron-right"

        :paginated="!loading && paginated"
        backend-pagination
        :total="meta?.total"
        :per-page="perPage"
        :current-page="meta?.current_page"
        @page-change="emits('onPageChange', $event)"
        :aria-next-label="$t('navigation.next_page')"
        :aria-previous-label="$t('navigation.previous_page')"
        :aria-page-label="$t('navigation.page')"
        :aria-current-label="$t('navigation.current_page')">

        <b-table-column field="id" label="ID" v-slot="props">
            <div class="is-flex">
                <div class="mr-2" v-if="selectionMode">
                    <b-checkbox
                        :native-value="props.row.id"
                        @input="toggleRow(props.row)"
                    />
                </div>

                {{ props.row.id }}

            </div>
        </b-table-column>

        <b-table-column field="created_at" :label="$t('token_transactions.fields.created_at.name')" v-slot="props">
            <time v-bind:datetime="props.row.created_at" v-bind:title="props.row.created_at">
                {{ props.row.created_at_diff }}
            </time>
            <b-icon icon="timer-sand" v-if="props.row.pending" :title="$t('token_transactions.transaction_pending')"/>
        </b-table-column>

        <b-table-column field="sender" :label="$t('token_transactions.fields.sender.name')" v-slot="props">
            {{ props.row.sender?.full_name }}
        </b-table-column>

        <b-table-column field="receiver" :label="$t('token_transactions.fields.receiver.name')" v-slot="props">
            {{ props.row.receiver?.full_name }}
        </b-table-column>

        <b-table-column field="amount" :label="$t('token_transactions.fields.amount.name')" v-slot="props">
            {{ props.row.amount }}
        </b-table-column>

        <b-table-column field="amount" :label="$t('token_transactions.fields.value.name')" v-slot="props">
            {{ props.row.value }} {{ props.row.currency }}
        </b-table-column>

        <b-table-column field="type" :label="$t('token_transactions.fields.type.name')" v-slot="props">
            <span v-if="props.row.type">
            {{ $t('token_transactions.transaction_types.' + props.row.type) }}
                </span>
        </b-table-column>

        <b-table-column field="money_transfer_status" :label="$t('token_transactions.fields.money_transfer_status.name')" v-slot="props">
            <span v-if="props.row.money_transfer_status">
                {{ $t('token_transactions.money_transfer_statuses.' + props.row.money_transfer_status) }}
            </span>
        </b-table-column>

        <b-table-column v-if="canDownloadConfirmation" field="" :label="$t('token_transactions.fields.confirmation.name')">
            <template v-slot:header="{ column }">
                <b-icon icon="file-chart-check-outline" :title="$t('token_transactions.fields.confirmation.name')"/>
            </template>
            <template v-slot="props">
                <section class="buttons">
                    <b-button type="is-link" size="is-small"
                              :class="{ 'is-loading': props.row.downloading }"
                              :disabled="props.row.downloading"
                              :title="$t('token_transactions.actions.download_confirmation')"
                              icon-right="file-download-outline"
                              @click="download(props.row)">
                    </b-button>
                </section>
            </template>

        </b-table-column>

        <template #empty>
            <div v-if="!loading" class="block has-text-grey has-text-centered mb-4 mt-4">
                {{ $t('token_transactions.labels.no_transactions')}}
            </div>
        </template>

        <template #detail="props" v-if="detailsView">
            <article class="media">
                <div class="media-content">
                    <div class="content">
                        <div class="fixed-grid has-2-cols has-1-cols-mobile">
                            <div class="grid">
                                <div class="cell">
                                    <p v-if="props.row.sender">
                                        <strong class="is-size-5">
                                            <span class="is-uppercase">{{ $t('token_transactions.fields.sender.name') }}</span>: {{ props.row.sender?.full_name }}
                                        </strong>
                                    </p>
                                    <div v-if="props.row.sender && props.row.sender?.role !== 'admin'">
                                        <p v-if="$auth.isAdmin()">
                                            <UserCompanies :user="props.row.sender"/>
                                        </p>
                                        <p>
                                            <b-icon size="is-small" icon="at"/>
                                            <span class="detail-label">{{ $t('users.fields.email.name') }}:</span>
                                            {{ props.row.sender?.email }}
                                        </p>
                                        <p v-if="props.row.sender?.role === 'employee' && props.row.sender?.phone_number">
                                            <b-icon size="is-small" icon="phone"/>
                                            <span class="detail-label">{{ $t('users.fields.phone_number.name') }}:</span>
                                            {{ props.row.sender?.phone_number }}
                                        </p>
                                        <p v-if="props.row.sender?.role === 'employee' && props.row.sender?.pesel">
                                            <b-icon size="is-small" icon="account"/>
                                            <span class="detail-label">{{ $t('users.fields.pesel.name') }}:</span>
                                            {{ props.row.sender?.pesel }}
                                        </p>
                                    </div>
                                </div>
                                <div class="cell">
                                    <p v-if="props.row.receiver">
                                        <strong class="is-size-5">
                                            <span class="is-uppercase">{{ $t('token_transactions.fields.receiver.name') }}</span>: {{ props.row.receiver?.full_name }}
                                        </strong>
                                    </p>
                                    <div v-if="props.row.receiver && props.row.sender?.role !== 'admin'">
                                        <p v-if="$auth.isAdmin()">
                                            <UserCompanies :user="props.row.receiver"/>
                                        </p>
                                        <p>
                                            <b-icon size="is-small" icon="at"/>
                                            <span class="detail-label">{{ $t('users.fields.email.name') }}:</span>
                                            {{ props.row.receiver?.email }}
                                        </p>
                                        <p v-if="props.row.role === 'employee' && props.row.receiver?.phone_number">
                                            <b-icon size="is-small" icon="phone"/>
                                            <span class="detail-label">{{ $t('users.fields.phone_number.name') }}:</span>
                                            {{ props.row.receiver?.phone_number }}
                                        </p>
                                        <p v-if="props.row.role === 'employee' && props.row.receiver?.pesel">
                                            <b-icon size="is-small" icon="account"/>
                                            <span class="detail-label">{{ $t('users.fields.pesel.name') }}:</span>
                                            {{ props.row.receiver?.pesel }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="props.row.comments">
                            <div>{{ $t('token_transactions.fields.comments.name') }}:</div>
                            <blockquote class="white-space-pre has-border">
                                {{ props.row.comments }}
                            </blockquote>
                        </div>
                    </div>
                </div>
            </article>
        </template>
        <template #bottom-left>
            <PerPageSelect v-if="showPerPage" v-model="perPage" @change="onPerPageChange" />
        </template>
    </b-table>
</template>
